import { TemplateType } from "./masterTemplate";
export type TemplateTypeValue = (typeof TemplateType)[keyof typeof TemplateType];

export const PLATFORM = {
    DOUBLECLICK: "doubleclick",
    DOUBLECLICK_SV: "doubleclick_sv",
    FLASHTALKING: "flashtalking",
    FACEBOOK: "facebook",
    OPENDC: "opendc",
    CREATIVEX: "creativex",
    GENERIC: "generic",
    GENERIC_LITE: "generic_lite",
    IMAGE: "image",
    PSD: "psd",
    INDESIGN: "indesign",
    VIDEO: "video",
} as const;

export const EXPORT_FORMAT = {
    // html (zip)
    GENERIC: "generic",
    GENERIC_LITE: "generic_lite",
    DOUBLECLICK: "doubleclick",
    DOUBLECLICK_SV: "doubleclick_sv",
    OPENDC: "opendc",
    FLASHTALKING: "flashtalking",
    // excel
    FLASHTALKING_EXCEL: "flashtalking_excel",
    // ft assets
    FLASHTALKING_ASSETS: "flashtalking_assets",
    // psd
    PSD_IMAGE_PNG: "psd_image_png",
    PSD_IMAGE_JPG: "psd_image_jpg",
    PSD: "psd",
    // indesign
    INDESIGN_PDF: "indesign_pdf",
    INDESIGN_JPG: "indesign_jpg",
    INDESIGN_TEMPLATE: "indesign",
    INDESIGN_BUNDLE: "indesign_bundle",
    // video
    VIDEO: "video",
    FACEBOOK: "facebook",
    // image
    IMAGE_PNG: "image_png",
    IMAGE_JPG: "image_jpg",
} as const;

export const HTML_EXPORT_FORMATS = [
    EXPORT_FORMAT.GENERIC,
    EXPORT_FORMAT.GENERIC_LITE,
    EXPORT_FORMAT.DOUBLECLICK,
    EXPORT_FORMAT.DOUBLECLICK_SV,
    EXPORT_FORMAT.FLASHTALKING,
    EXPORT_FORMAT.OPENDC,
] as const;

export type ExportFormatType = (typeof EXPORT_FORMAT)[keyof typeof EXPORT_FORMAT];

export const HTML_IMAGE_EXPORT_FORMATS = [
    EXPORT_FORMAT.IMAGE_PNG,
    EXPORT_FORMAT.IMAGE_JPG,
] as const satisfies readonly ExportFormatType[];

export type ScreenshotImageType = "jpeg" | "png";

export const IMAGE_SCALES = [1, 2, 3, 4] as const;

export type ImageScale = (typeof IMAGE_SCALES)[number];

export const DEFAULT_IMAGE_SCALE: ImageScale = 1;

export const INDESIGN_COLOR_SPACES = ["rgb", "cmyk"] as const;

export type InDesignColorSpace = (typeof INDESIGN_COLOR_SPACES)[number];

export const DEFAULT_INDESIGN_COLOR_SPACE: InDesignColorSpace = "rgb";

export const isInDesignColorSpace = (value: unknown): value is InDesignColorSpace =>
    INDESIGN_COLOR_SPACES.includes(value as InDesignColorSpace);

export type ExportOptionSetting =
    | "dcProfileId"
    | "imageScale"
    | "optimiseFontFiles"
    | "optimiseTextFiles"
    | "optimiseImages"
    | "includeBackupImage"
    | "ftBrowserExclusions"
    | "ftClickTagCount"
    | "colorSpace";

export const EXPORT_SERVICE = {
    PackDownloader: "PackDownloader",
    ScreenshotDownloader: "ScreenshotDownloader",
    PsdDownloader: "PsdDownloader",
    VideoDownloader: "VideoDownloader",
    ADOBE_INDESIGN_DOWNLOAD: "ADOBE_INDESIGN_DOWNLOAD",
    PUBFT_DOWNLOAD_EXCEL: "PUBFT_DOWNLOAD_EXCEL",
    PUBFT_DOWNLOAD_ASSETS: "PUBFT_DOWNLOAD_ASSETS",
};
export type ExportServiceType = (typeof EXPORT_SERVICE)[keyof typeof EXPORT_SERVICE];

export type ExportOptionConfig = {
    label: string;
    templateTypes: readonly TemplateTypeValue[];
    exportService: ExportServiceType;
    settings?: readonly ExportOptionSetting[];
    imageType?: ScreenshotImageType;
};

/**
 * `imageType` is optional in general but mandatory for the HTML5 image formats, so adding one
 * without declaring what Chromium should render is a compile error here rather than an
 * "Unsupported screenshot export format" at runtime.
 */
type ExportConfigFor<F extends ExportFormatType> = F extends (typeof HTML_IMAGE_EXPORT_FORMATS)[number]
    ? ExportOptionConfig & Required<Pick<ExportOptionConfig, "imageType">>
    : ExportOptionConfig;

export const EXPORT_CONFIGS_BY_TEMPLATE_TYPE = {
    HTML5: [
        EXPORT_FORMAT.DOUBLECLICK,
        EXPORT_FORMAT.DOUBLECLICK_SV,
        EXPORT_FORMAT.OPENDC,
        EXPORT_FORMAT.FLASHTALKING,
        EXPORT_FORMAT.FLASHTALKING_EXCEL,
        EXPORT_FORMAT.FLASHTALKING_ASSETS,
        EXPORT_FORMAT.GENERIC,
        EXPORT_FORMAT.GENERIC_LITE,
        EXPORT_FORMAT.IMAGE_PNG,
        EXPORT_FORMAT.IMAGE_JPG,
        EXPORT_FORMAT.VIDEO,
    ],
    PSD: [EXPORT_FORMAT.PSD_IMAGE_PNG, EXPORT_FORMAT.PSD_IMAGE_JPG, EXPORT_FORMAT.PSD],
    AE: [EXPORT_FORMAT.VIDEO],
    INDESIGN: [
        EXPORT_FORMAT.INDESIGN_PDF,
        EXPORT_FORMAT.INDESIGN_JPG,
        EXPORT_FORMAT.INDESIGN_TEMPLATE,
        EXPORT_FORMAT.INDESIGN_BUNDLE,
    ],
} as const satisfies Record<TemplateTypeValue, readonly ExportFormatType[]>;

export const EXPORT_CONFIGS = {
    [EXPORT_FORMAT.DOUBLECLICK]: {
        label: "Google Studio HTML",
        templateTypes: [TemplateType.HTML5],
        exportService: EXPORT_SERVICE.PackDownloader,
        settings: ["optimiseFontFiles", "optimiseTextFiles", "optimiseImages"],
    },
    [EXPORT_FORMAT.DOUBLECLICK_SV]: {
        label: "Google Studio Dynamic HTML",
        templateTypes: [TemplateType.HTML5],
        exportService: EXPORT_SERVICE.PackDownloader,
        settings: ["dcProfileId", "optimiseFontFiles", "optimiseTextFiles", "optimiseImages"],
    },
    [EXPORT_FORMAT.OPENDC]: {
        label: "Open Media Studio HTML",
        templateTypes: [TemplateType.HTML5],
        exportService: EXPORT_SERVICE.PackDownloader,
        settings: ["optimiseFontFiles", "optimiseTextFiles", "optimiseImages"],
    },
    [EXPORT_FORMAT.FLASHTALKING]: {
        label: "Flashtalking HTML",
        templateTypes: [TemplateType.HTML5],
        exportService: EXPORT_SERVICE.PackDownloader,
        settings: [
            "optimiseFontFiles",
            "optimiseTextFiles",
            "optimiseImages",
            "ftBrowserExclusions",
            "ftClickTagCount",
        ],
    },
    [EXPORT_FORMAT.FLASHTALKING_EXCEL]: {
        label: "Flashtalking Excel",
        templateTypes: [TemplateType.HTML5],
        exportService: EXPORT_SERVICE.PUBFT_DOWNLOAD_EXCEL,
    },
    [EXPORT_FORMAT.FLASHTALKING_ASSETS]: {
        label: "Flashtalking Assets",
        templateTypes: [TemplateType.HTML5],
        exportService: EXPORT_SERVICE.PUBFT_DOWNLOAD_ASSETS,
    },
    [EXPORT_FORMAT.GENERIC]: {
        label: "Generic HTML",
        templateTypes: [TemplateType.HTML5],
        exportService: EXPORT_SERVICE.PackDownloader,
        settings: ["optimiseFontFiles", "optimiseTextFiles", "optimiseImages", "includeBackupImage"],
    },
    [EXPORT_FORMAT.GENERIC_LITE]: {
        label: "Generic Lite HTML",
        templateTypes: [TemplateType.HTML5],
        exportService: EXPORT_SERVICE.PackDownloader,
    },
    [EXPORT_FORMAT.IMAGE_PNG]: {
        label: "Image (PNG)",
        templateTypes: [TemplateType.HTML5],
        exportService: EXPORT_SERVICE.ScreenshotDownloader,
        settings: ["imageScale"],
        imageType: "png",
    },
    [EXPORT_FORMAT.IMAGE_JPG]: {
        label: "Image (JPG)",
        templateTypes: [TemplateType.HTML5],
        exportService: EXPORT_SERVICE.ScreenshotDownloader,
        settings: ["imageScale"],
        imageType: "jpeg",
    },
    [EXPORT_FORMAT.PSD_IMAGE_PNG]: {
        label: "Image (PNG)",
        templateTypes: [TemplateType.PSD],
        exportService: EXPORT_SERVICE.PsdDownloader,
    },
    [EXPORT_FORMAT.PSD_IMAGE_JPG]: {
        label: "Image (JPG)",
        templateTypes: [TemplateType.PSD],
        exportService: EXPORT_SERVICE.PsdDownloader,
    },
    [EXPORT_FORMAT.PSD]: {
        label: TemplateType.PSD,
        templateTypes: [TemplateType.PSD],
        exportService: EXPORT_SERVICE.PsdDownloader,
    },
    [EXPORT_FORMAT.VIDEO]: {
        label: "Video",
        templateTypes: [TemplateType.HTML5, TemplateType.AE],
        exportService: EXPORT_SERVICE.VideoDownloader,
    },
    [EXPORT_FORMAT.FACEBOOK]: {
        label: "Meta Ads Manager Video",
        templateTypes: [TemplateType.HTML5],
        exportService: EXPORT_SERVICE.VideoDownloader,
    },
    [EXPORT_FORMAT.INDESIGN_PDF]: {
        label: "PDF",
        templateTypes: [TemplateType.INDESIGN],
        exportService: EXPORT_SERVICE.ADOBE_INDESIGN_DOWNLOAD,
        settings: ["colorSpace"],
    },
    [EXPORT_FORMAT.INDESIGN_JPG]: {
        label: "JPG",
        templateTypes: [TemplateType.INDESIGN],
        exportService: EXPORT_SERVICE.ADOBE_INDESIGN_DOWNLOAD,
        settings: ["colorSpace"],
    },
    [EXPORT_FORMAT.INDESIGN_TEMPLATE]: {
        label: "InDesign file (.indd)",
        templateTypes: [TemplateType.INDESIGN],
        exportService: EXPORT_SERVICE.ADOBE_INDESIGN_DOWNLOAD,
    },
    [EXPORT_FORMAT.INDESIGN_BUNDLE]: {
        label: "InDesign Bundle (.zip)",
        templateTypes: [TemplateType.INDESIGN],
        exportService: EXPORT_SERVICE.ADOBE_INDESIGN_DOWNLOAD,
    },
} as const satisfies { [F in ExportFormatType]: ExportConfigFor<F> };

/**
 * The Chromium screenshot type a format renders to, or undefined if it is not a screenshot export.
 *
 * The lookup is guarded rather than direct: download jobs persist their `exportFormat`, so a job
 * created before the `image_png_2x` / `image_jpg_2x` presets were retired can still reach the
 * renderer carrying one. Those resolve to undefined so the caller can fail with a readable message
 * instead of dereferencing a missing config.
 */
export const getScreenshotImageType = (exportFormat: ExportFormatType): ScreenshotImageType | undefined => {
    const exportConfig: ExportOptionConfig | undefined = EXPORT_CONFIGS[exportFormat];

    return exportConfig?.imageType;
};

export const PUBLISH_PROVIDERS = {
    DOUBLECLICK: PLATFORM.DOUBLECLICK,
    OPENDC: PLATFORM.OPENDC,
    CREATIVEX: PLATFORM.CREATIVEX,
    FACEBOOK: PLATFORM.FACEBOOK,
    ALL: "ALL",
} as const;

export type PublishProvidersType = (typeof PUBLISH_PROVIDERS)[keyof typeof PUBLISH_PROVIDERS];

export const DYCE_PLATFORM = {
    DOUBLECLICK: PLATFORM.DOUBLECLICK,
    DOUBLECLICK_SV: PLATFORM.DOUBLECLICK_SV,
    GENERIC: PLATFORM.GENERIC,
    FLASHTALKING: PLATFORM.FLASHTALKING,
    OPENDC: PLATFORM.OPENDC,
    GENERIC_LITE: PLATFORM.GENERIC_LITE,
    PREVIEW: "preview",
} as const;

export type DycePlatformType = (typeof DYCE_PLATFORM)[keyof typeof DYCE_PLATFORM];
